import { useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';

const Privacy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Diamond Club Matchmaking';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Privacy Policy for Diamond Club Matchmaking. Learn how we collect, use, and protect your personal information.');
    }
  }, []);

  return (
    <Layout>
      <section className="min-h-screen bg-black-deep grain-overlay">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          {/* Page Title */}
          <div className="text-center mb-12">
            <h1 className="font-serif text-display-sm md:text-display text-cream mb-2">
              Privacy Policy
            </h1>
            <p className="font-serif text-xl text-gold mb-4">
              Diamond Club Matchmaking
            </p>
            <p className="font-sans text-cream-muted text-sm">
              Effective Date: January 1, 2025
            </p>
          </div>
          
          {/* Introduction */}
          <div className="mb-12">
            <p className="font-sans text-cream-muted text-base leading-relaxed">
              Diamond Club Matchmaking ("we," "us," or "our") respects your privacy and is committed to protecting your personal information.
            </p>
          </div>
          
          {/* Sections */}
          <div className="space-y-10">
            {/* Section 1 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                1. Information We Collect
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed mb-4">
                We may collect:
              </p>
              <ul className="font-sans text-cream-muted text-base leading-relaxed space-y-2 ml-6">
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Name, email, phone number</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Demographic and lifestyle information</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Photos and profile details</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Payment information (processed securely by third-party providers)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Communications with our team</span>
                </li>
              </ul>
            </div>

            {/* Section 2 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                2. How We Use Your Information
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed mb-4">
                Your information is used to:
              </p>
              <ul className="font-sans text-cream-muted text-base leading-relaxed space-y-2 ml-6">
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Provide matchmaking and member services</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Facilitate events or introductions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Communicate with you</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Improve our offerings</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Maintain safety and integrity</span>
                </li>
              </ul>
            </div>

            {/* Section 3 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                3. Confidentiality & Discretion
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                Diamond Club values privacy, discretion, and confidentiality, especially for high-profile and high-net-worth members. We do not sell personal data.
              </p>
            </div>

            {/* Section 4 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                4. Sharing Your Information
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed mb-4">
                We never sell your data. We may share limited info with:
              </p>
              <ul className="font-sans text-cream-muted text-base leading-relaxed space-y-2 ml-6">
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Secure third-party tools (e.g., email platforms, payment processors)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Team members or contractors working under confidentiality</span>
                </li>
              </ul>
            </div>

            {/* Section 5 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                5. Your Rights
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed mb-4">
                You may:
              </p>
              <ul className="font-sans text-cream-muted text-base leading-relaxed space-y-2 ml-6">
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Request access to your data</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Ask for corrections or deletions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Opt out of email communications anytime</span>
                </li>
              </ul>
              <p className="font-sans text-cream-muted text-base leading-relaxed mt-4">
                To do so, contact: <a href="mailto:Info@diamondclubmatchmaking.com" className="text-gold hover:text-gold-light transition-colors">Info@diamondclubmatchmaking.com</a>
              </p>
            </div>

            {/* Section 6 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                6. Security
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                We take data protection seriously. Our site uses SSL encryption, and your info is stored using secure methods.
              </p>
            </div>

            {/* Section 7 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                7. Cookies
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                Our site uses cookies to enhance your experience. You can disable cookies in your browser settings at any time.
              </p>
            </div>

            {/* Section 8 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                8. Updates
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                This policy may be updated from time to time. Please check back for the latest version.
              </p>
            </div>

            {/* Section 9 - Text Messaging */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                9. Text Messaging Services
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed mb-4">
                When you provide your phone number and opt-in to Diamond Club Matchmaking's text messaging service, you agree to receive SMS/text messages from us regarding matchmaking services, appointments, match notifications, and account updates. Message frequency varies based on your account activity. Message and data rates may apply.
              </p>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                You may opt-out at any time by texting STOP to any message. Text HELP for assistance. Your phone number will not be shared with third parties for marketing purposes. We store your number securely and only use it for service-related communications you've consented to receive.
              </p>
            </div>

            {/* Contact */}
            <div className="pt-6 border-t border-gold/20">
              <p className="font-sans text-cream text-base leading-relaxed">
                💌 Questions?
              </p>
              <p className="font-sans text-cream-muted text-base leading-relaxed mt-2">
                Reach out anytime: <a href="mailto:Info@diamondclubmatchmaking.com" className="text-gold hover:text-gold-light transition-colors">Info@diamondclubmatchmaking.com</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Privacy;
